import React, { useEffect, useRef, useState, useCallback } from 'react';

const API = 'https://n293vk0zm6.execute-api.us-east-1.amazonaws.com/prod';
const CLUSTER_COLORS = ['#0D9488', '#6366F1', '#F59E0B', '#EC4899'];

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000)    return (n / 1000).toFixed(0) + 'K';
  return String(n);
}

function getTodayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
}

function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
}

function computeClusterStats(points) {
  return [0, 1, 2, 3].map(c => {
    const members = points.filter(p => p.cluster === c);
    if (members.length === 0) return {
      cluster: c, total: 0, flagged: 0,
      avgPlaycount: 0, avgListeners: 0, label: 'No data'
    };
    const avgPlaycount = Math.round(
      members.reduce((s, p) => s + p.playcount, 0) / members.length
    );
    const avgListeners = Math.round(
      members.reduce((s, p) => s + p.listeners, 0) / members.length
    );
    return {
      cluster: c,
      total: members.length,
      flagged: members.filter(p => p.flagged).length,
      avgPlaycount,
      avgListeners,
    };
  });
}

function getDynamicLabel(stats, clusterIdx) {
  if (stats[clusterIdx].total === 0) return 'No data';

  const sorted = [...stats]
    .filter(s => s.total > 0)
    .sort((a, b) => (b.avgPlaycount + b.avgListeners) - (a.avgPlaycount + a.avgListeners));

  const rank = sorted.findIndex(s => s.cluster === clusterIdx);
  const s = stats[clusterIdx];
  const ratio = s.avgListeners > 0 ? s.avgPlaycount / s.avgListeners : 0;

  if (rank === 0) return 'Viral — massive reach';
  if (rank === sorted.length - 1) return 'Emerging — building momentum';
  if (ratio > 15) return 'Mainstream — broad appeal';
  return 'Niche — loyal fanbase';
}

export default function ClusterMap({ onAlertClick, alerts = [] }) {
  const canvasRef   = useRef(null);
  const tooltipRef  = useRef(null);
  const animRef     = useRef(null);
  const pointsRef   = useRef([]);

  const [points, setPoints]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [noData, setNoData]           = useState(false);
  const [currentDate, setCurrentDate] = useState(getTodayET());
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [animProgress, setAnimProgress]     = useState(0);

  const today = getTodayET();

  const fetchClusters = useCallback(async (date) => {
    setLoading(true);
    setNoData(false);
    setPoints([]);
    setAnimProgress(0);
    try {
      const res  = await fetch(`${API}/clusters?date=${date}`);
      const data = await res.json();
      const items = data.clusters || [];
      if (items.length === 0) { setNoData(true); return; }
      const mapped = items.map(t => ({
        name:          t.name,
        artist:        t.artist,
        rank:          t.rank,
        playcount:     t.playcount,
        listeners:     t.listeners,
        flagged:       t.flagged || false,
        cluster:       typeof t.cluster === 'number' ? t.cluster : -1,
        shifted:       t.cluster_shifted || false,
        models_fired:  t.models_fired || '',
        anomaly_score: t.anomaly_score || null,
      }));
      setPoints(mapped);
      pointsRef.current = mapped;

      const start = Date.now();
      const duration = 600;
      function animate() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 2);
        setAnimProgress(eased);
        if (progress < 1) animRef.current = requestAnimationFrame(animate);
      }
      animRef.current = requestAnimationFrame(animate);
    } catch {
      setNoData(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClusters(currentDate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [currentDate]);

  useEffect(() => {
    if (points.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const W   = canvas.offsetWidth;
    const H   = 360;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const pad = { l: 52, r: 16, t: 16, b: 40 };
    const cw  = W - pad.l - pad.r;
    const ch  = H - pad.t - pad.b;

    const maxP = Math.max(...points.map(p => p.playcount));
    const maxL = Math.max(...points.map(p => p.listeners));

    const isDark    = document.documentElement.getAttribute('data-theme') === 'dark';
    const gridColor = isDark ? '#1F2937' : '#F3F4F6';
    const axisColor = isDark ? '#374151' : '#E5E7EB';
    const textColor = isDark ? '#6B7280' : '#9CA3AF';

    ctx.clearRect(0, 0, W, H);

    ctx.strokeStyle = gridColor;
    ctx.lineWidth   = 0.5;
    for (let i = 0; i <= 4; i++) {
      ctx.beginPath();
      ctx.moveTo(pad.l + cw * i / 4, pad.t);
      ctx.lineTo(pad.l + cw * i / 4, pad.t + ch);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pad.l, pad.t + ch * i / 4);
      ctx.lineTo(pad.l + cw, pad.t + ch * i / 4);
      ctx.stroke();
    }

    ctx.strokeStyle = axisColor;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(pad.l, pad.t + ch);
    ctx.lineTo(pad.l + cw, pad.t + ch);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pad.l, pad.t);
    ctx.lineTo(pad.l, pad.t + ch);
    ctx.stroke();

    ctx.fillStyle = textColor;
    ctx.font      = '12px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('Playcount →', pad.l + cw / 2, H - 4);
    ctx.save();
    ctx.translate(10, pad.t + ch / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Listeners →', 0, 0);
    ctx.restore();

    ctx.font      = '10px system-ui';
    ctx.textAlign = 'center';
    ctx.fillStyle = textColor;
    for (let i = 0; i <= 4; i++) {
      const val   = maxP * i / 4;
      const x     = pad.l + cw * i / 4;
      const label = val >= 1000000
        ? (val / 1000000).toFixed(1) + 'M'
        : (val / 1000).toFixed(0) + 'K';
      ctx.fillText(label, x, pad.t + ch + 14);
    }
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const val   = maxL * i / 4;
      const y     = pad.t + ch - ch * i / 4;
      const label = val >= 1000000
        ? (val / 1000000).toFixed(1) + 'M'
        : (val / 1000).toFixed(0) + 'K';
      ctx.fillText(label, pad.l - 6, y + 3);
    }

    points.forEach(p => {
      const x        = pad.l + (p.playcount / maxP) * cw;
      const y        = pad.t + ch - (p.listeners / maxL) * ch;
      const dotSize  = p.rank <= 10 ? 6 : 4;
      const isHovered = hoveredCluster !== null && p.cluster !== hoveredCluster && !p.shifted;
      const alpha    = animProgress * (isHovered ? 0.15 : 1.0);

      ctx.globalAlpha = alpha;

      if (p.shifted) {
        [18, 13].forEach(r => {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.strokeStyle = r === 18 ? '#0D948825' : '#0D948850';
          ctx.lineWidth   = 1;
          ctx.stroke();
        });
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.strokeStyle = '#0D9488';
        ctx.lineWidth   = 2;
        ctx.stroke();
      } else if (p.cluster >= 0) {
        ctx.beginPath();
        ctx.arc(x, y, dotSize, 0, Math.PI * 2);
        ctx.fillStyle = CLUSTER_COLORS[p.cluster] + 'CC';
        ctx.fill();
        if (p.flagged) {
          ctx.beginPath();
          ctx.arc(x, y, dotSize + 3, 0, Math.PI * 2);
          ctx.strokeStyle = CLUSTER_COLORS[p.cluster];
          ctx.lineWidth   = 1.5;
          ctx.stroke();
        }
      } else {
        ctx.beginPath();
        ctx.arc(x, y, dotSize, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? '#374151' : '#D1D5DB';
        ctx.fill();
      }

      ctx.globalAlpha = 1.0;
    });

    const tooltip = tooltipRef.current;
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx   = e.clientX - rect.left;
      const my   = e.clientY - rect.top;
      let found  = null;

      points.forEach(p => {
        const x = pad.l + (p.playcount / maxP) * cw;
        const y = pad.t + ch - (p.listeners / maxL) * ch;
        if (Math.abs(mx - x) < 10 && Math.abs(my - y) < 10) found = p;
      });

      if (found) {
        const clusterInfo = found.shifted
          ? '⚡ Cluster shifted — pre-viral signal'
          : found.cluster >= 0
            ? `Cluster ${found.cluster}${found.flagged ? ' · 🔔 Anomaly flagged' : ''}`
            : 'Insufficient history';
        tooltip.style.display = 'block';
        tooltip.style.left    = (pad.l + (found.playcount / maxP) * cw + 12) + 'px';
        tooltip.style.top     = (pad.t + ch - (found.listeners / maxL) * ch - 70) + 'px';
        tooltip.innerHTML     = `
          <div style="font-weight:600;margin-bottom:3px">${found.name}</div>
          <div style="color:var(--muted);font-size:10px">${found.artist}</div>
          <div style="margin-top:4px;font-size:11px">Rank #${found.rank}</div>
          <div style="font-size:11px">${fmt(found.playcount)} plays · ${fmt(found.listeners)} listeners</div>
          <div style="margin-top:4px;font-size:10px;color:var(--accent)">${clusterInfo}</div>
        `;
        canvas.style.cursor = found.flagged ? 'pointer' : 'default';
      } else {
        tooltip.style.display = 'none';
        canvas.style.cursor   = 'default';
      }
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx   = e.clientX - rect.left;
      const my   = e.clientY - rect.top;
      points.forEach(p => {
        const x = pad.l + (p.playcount / maxP) * cw;
        const y = pad.t + ch - (p.listeners / maxL) * ch;
        if (Math.abs(mx - x) < 10 && Math.abs(my - y) < 10 && p.flagged) {
          const matchedAlert = alerts.find(a =>
            a.name?.toLowerCase() === p.name?.toLowerCase()
          );
          if (matchedAlert && onAlertClick) onAlertClick(matchedAlert);
        }
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
    };
  }, [points, hoveredCluster, animProgress, alerts, onAlertClick]);

  const clusterStats  = computeClusterStats(points);
  const shiftedPoints = points.filter(p => p.shifted);
  const unassigned    = points.filter(p => p.cluster === -1).length;
  const isToday       = currentDate === today;

  return (
    <div>
      {/* Date navigation */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '14px'
      }}>
        <button
          onClick={() => setCurrentDate(d => addDays(d, -1))}
          style={{
            padding: '6px 14px', borderRadius: '6px', fontSize: '12px',
            border: '1px solid var(--border)', background: 'var(--surface)',
            color: 'var(--sub)', cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        <div style={{
          flex: 1, textAlign: 'center', padding: '7px 16px',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: '6px', fontSize: '13px', fontWeight: 500,
          color: 'var(--text)'
        }}>
          {formatDisplayDate(currentDate)}
          {isToday && (
            <span style={{
              marginLeft: '8px', fontSize: '10px', fontWeight: 600,
              background: 'var(--accent-bg)', color: 'var(--accent)',
              border: '1px solid var(--accent-bd)', borderRadius: '20px',
              padding: '1px 8px'
            }}>
              Today
            </span>
          )}
        </div>
        <button
          onClick={() => setCurrentDate(d => addDays(d, 1))}
          disabled={isToday}
          style={{
            padding: '6px 14px', borderRadius: '6px', fontSize: '12px',
            border: '1px solid var(--border)', background: 'var(--surface)',
            color: isToday ? 'var(--border)' : 'var(--sub)',
            cursor: isToday ? 'default' : 'pointer'
          }}
        >
          Next →
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        background: 'var(--accent-bg)', border: '1px solid var(--accent-bd)',
        borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
        fontSize: '12px', color: 'var(--accent)'
      }}>
        All 50 tracks plotted by playcount and listeners. Colored dots show cluster assignments — grey dots have insufficient history. Anomaly rings highlight flagged tracks. Click a flagged dot to open its alert.
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)', fontSize: '13px' }}>
          Loading cluster map...
        </div>
      )}

      {!loading && noData && (
        <div style={{
          textAlign: 'center', padding: '40px', color: 'var(--muted)',
          fontSize: '13px', background: 'var(--surface)',
          borderRadius: '10px', border: '1px solid var(--border)'
        }}>
          No cluster data available for {formatDisplayDate(currentDate)}.
        </div>
      )}

      {!loading && !noData && (
        <>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--sub)' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: CLUSTER_COLORS[i], flexShrink: 0 }} />
                Cluster {i} — {getDynamicLabel(clusterStats, i)}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--sub)' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff', border: '2px solid var(--accent)', flexShrink: 0 }} />
              Cluster shifted
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--sub)' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--border)', flexShrink: 0 }} />
              No history ({unassigned} tracks)
            </div>
          </div>

          {/* Canvas */}
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: '10px', padding: '12px', marginBottom: '12px',
            position: 'relative'
          }}>
            <canvas
              ref={canvasRef}
              className="cluster-map-canvas"
              style={{ width: '100%', display: 'block', height: '360px' }}
            />
            <div ref={tooltipRef} style={{
              display: 'none', position: 'absolute',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '8px', padding: '8px 12px',
              fontSize: '11px', color: 'var(--text)',
              pointerEvents: 'none', zIndex: 10,
              whiteSpace: 'nowrap', lineHeight: '1.6',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }} />
          </div>

          {/* Cluster summary cards */}
          <div className="cluster-summary-grid" style={{ gap: '8px', marginBottom: '12px' }}>
            {clusterStats.map(c => (
              <div
                key={c.cluster}
                onMouseEnter={() => setHoveredCluster(c.cluster)}
                onMouseLeave={() => setHoveredCluster(null)}
                style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: '8px', padding: '12px 14px',
                  borderTop: `3px solid ${CLUSTER_COLORS[c.cluster]}`,
                  cursor: 'default',
                  transition: 'box-shadow 0.2s',
                  boxShadow: hoveredCluster === c.cluster
                    ? 'var(--card-shadow-hover)'
                    : 'var(--card-shadow)'
                }}
              >
                <div style={{
                  fontSize: '10px', color: CLUSTER_COLORS[c.cluster],
                  fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.4px', marginBottom: '4px'
                }}>
                  Cluster {c.cluster}
                </div>
                <div style={{
                  fontSize: '11px', color: 'var(--text)',
                  fontWeight: 500, marginBottom: '6px'
                }}>
                  {getDynamicLabel(clusterStats, c.cluster)}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', lineHeight: 1 }}>
                  {c.total}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                  tracks · {c.flagged > 0
                    ? <span style={{ color: 'var(--accent)' }}>{c.flagged} flagged</span>
                    : 'none flagged'}
                </div>
                {c.total > 0 && (
                  <div style={{
                    marginTop: '8px', paddingTop: '8px',
                    borderTop: '1px solid var(--border)',
                    fontSize: '10px', color: 'var(--muted)'
                  }}>
                    <div>Avg plays: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{fmt(c.avgPlaycount)}</span></div>
                    <div>Avg listeners: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{fmt(c.avgListeners)}</span></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Shifted tracks */}
          {shiftedPoints.length > 0 && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '10px', padding: '14px'
            }}>
              <div style={{
                fontSize: '10px', color: 'var(--muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px'
              }}>
                Tracks that shifted clusters since yesterday
              </div>
              {shiftedPoints.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 0',
                  borderBottom: i < shiftedPoints.length - 1 ? '1px solid var(--border)' : 'none'
                }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                      Rank #{p.rank} · {fmt(p.playcount)} plays · {fmt(p.listeners)} listeners
                    </div>
                  </div>
                  <div style={{
                    fontSize: '10px', background: 'var(--accent-bg)', color: 'var(--accent)',
                    padding: '2px 9px', borderRadius: '20px', border: '1px solid var(--accent-bd)',
                    whiteSpace: 'nowrap'
                  }}>
                    Cluster shifted
                  </div>
                </div>
              ))}
            </div>
          )}

          {shiftedPoints.length === 0 && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '10px', padding: '14px',
              fontSize: '12px', color: 'var(--muted)', textAlign: 'center'
            }}>
              No cluster shifts detected for {formatDisplayDate(currentDate)}.
            </div>
          )}
        </>
      )}
    </div>
  );
}
